import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, Clock, CheckCircle, AlertCircle, User, MessageSquare, Send, Reply, Check } from 'lucide-react';
import { collection, getDocs, query, orderBy, where, updateDoc, doc, addDoc, serverTimestamp, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useToast } from '@/hooks/useToast';
import { formatUkDate, formatUkDateTime } from '@/lib/adminFormat';
// Push notifications removed - service workers disabled
// import { sendPushNotification } from '@/lib/pushNotifications';

const ContactSubmissionsManager = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);
  const [threadMessages, setThreadMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadSubmissions();
  }, []);

  // Load full thread (all admin + artist messages) for a submission
  const loadThread = (submissionId) => {
    setLoadingThread(true);
    setThreadMessages([]);
    const q = query(
      collection(db, 'support_responses'),
      where('submissionId', '==', submissionId),
      orderBy('createdAt', 'asc')
    );
    return onSnapshot(q, (snap) => {
      setThreadMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingThread(false);
    }, (err) => {
      console.error('Error loading thread:', err);
      setLoadingThread(false);
    });
  };

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const submissionsQuery = query(
        collection(db, 'contact_submissions'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(submissionsQuery);
      const submissionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSubmissions(submissionsData);
    } catch (error) {
      console.error('Error loading contact submissions:', error);
      toast({
        title: "Error",
        description: "Failed to load contact submissions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSubmissionStatus = async (submissionId, newStatus) => {
    try {
      await updateDoc(doc(db, 'contact_submissions', submissionId), {
        status: newStatus,
        updatedAt: new Date()
      });
      
      setSubmissions(prev => prev.map(sub => 
        sub.id === submissionId ? { ...sub, status: newStatus } : sub
      ));
      
      toast({
        title: "Status Updated",
        description: `Submission marked as ${newStatus}`
      });
    } catch (error) {
      console.error('Error updating submission status:', error);
      toast({
        title: "Error",
        description: "Failed to update submission status",
        variant: "destructive"
      });
    }
  };

  const submitResponse = async (markAsResolved = false) => {
    if (!responseMessage.trim() || !selectedSubmission) {
      console.error('❌ Missing required data:', { responseMessage, selectedSubmission });
      toast({
        title: "Error",
        description: "Missing required data to send response",
        variant: "destructive"
      });
      return;
    }

    console.log('🚀 Submitting response:', {
      markAsResolved,
      submissionId: selectedSubmission.id,
      userId: selectedSubmission.userId,
      userEmail: selectedSubmission.userEmail,
      messageLength: responseMessage.trim().length
    });

    setIsSubmittingResponse(true);
    try {
      // Create response document
      console.log('📝 Creating support_responses document...');
      const responseDoc = await addDoc(collection(db, 'support_responses'), {
        submissionId: selectedSubmission.id,
        userId: selectedSubmission.userId,
        userEmail: selectedSubmission.userEmail,
        message: responseMessage.trim(),
        fromAdmin: true,
        createdAt: serverTimestamp(),
        read: false
      });
      console.log('✅ Support response document created:', responseDoc.id);

      // Send notification to user
      console.log('🔔 Creating admin_notifications document...');
      const notificationDoc = await addDoc(collection(db, 'admin_notifications'), {
        title: markAsResolved ? '✅ Support Request Resolved' : '💬 Support Response',
        message: markAsResolved 
          ? `Your support request "${selectedSubmission.subject}" has been resolved. Thank you for contacting us!`
          : `You have a new response to your support request: "${selectedSubmission.subject}"`,
        type: 'support_response',
        targetAudience: 'specific',
        targetUserId: selectedSubmission.userId,
        targetUserIds: [selectedSubmission.userId],
        status: 'sent',
        recipientCount: 1,
        sentAt: serverTimestamp(),
        data: {
          submissionId: selectedSubmission.id,
          subject: selectedSubmission.subject,
          resolved: markAsResolved
        },
        createdAt: serverTimestamp(),
        read: false
      });
      console.log('✅ Admin notification created:', notificationDoc.id);

      // Push notifications removed - service workers disabled

      // Update submission status
      console.log('📊 Updating submission status...');
      const newStatus = markAsResolved ? 'resolved' : (selectedSubmission.status === 'new' ? 'in_progress' : selectedSubmission.status);
      await updateSubmissionStatus(selectedSubmission.id, newStatus);
      console.log('✅ Submission status updated to:', newStatus);

      toast({
        title: markAsResolved ? "Request Resolved" : "Response Sent",
        description: markAsResolved 
          ? "The support request has been marked as resolved"
          : "Your response has been sent to the artist and they will be notified."
      });

      setResponseMessage('');
      setShowResponseForm(false);
      setShowDetails(false);

    } catch (error) {
      console.error('❌ Error submitting response:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        selectedSubmission: selectedSubmission,
        responseMessage: responseMessage
      });
      toast({
        title: "Error",
        description: `Failed to send response: ${error.message || 'Unknown error'}`,
        variant: "destructive"
      });
    } finally {
      setIsSubmittingResponse(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return <Badge variant="destructive" className="bg-red-600">New</Badge>;
      case 'in_progress':
        return <Badge variant="secondary" className="bg-yellow-600">In Progress</Badge>;
      case 'resolved':
        return <Badge variant="default" className="bg-green-600">Resolved</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-yellow-400" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      default:
        return <MessageSquare className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
            <span className="ml-2 text-gray-400">Loading contact submissions...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Mail className="h-5 w-5 text-purple-400" />
            Contact Submissions
          </CardTitle>
          <p className="text-gray-400">
            Manage support requests and contact form submissions from artists
          </p>
        </CardHeader>
        <CardContent>
          {submissions.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">No contact submissions yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-gray-400">Artist</TableHead>
                    <TableHead className="text-gray-400">Subject</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Date</TableHead>
                    <TableHead className="text-gray-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((submission) => (
                    <TableRow key={submission.id}>
                      <TableCell className="text-white">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="font-medium">{submission.userEmail}</div>
                            <div className="text-sm text-gray-400">Artist</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-white">
                        <div className="max-w-xs truncate" title={submission.subject}>
                          {submission.subject}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(submission.status)}
                          {getStatusBadge(submission.status)}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {submission.createdAt?.toDate
                          ? formatUkDate(submission.createdAt)
                          : 'Unknown'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedSubmission(submission);
                              setShowDetails(true);
                              loadThread(submission.id);
                            }}
                            className="border-gray-600 text-gray-300 hover:bg-gray-700"
                          >
                            View
                          </Button>
                          {submission.status === 'new' && (
                            <Button
                              size="sm"
                              onClick={() => updateSubmissionStatus(submission.id, 'in_progress')}
                              className="bg-yellow-600 hover:bg-yellow-700"
                            >
                              Start
                            </Button>
                          )}
                          {submission.status === 'in_progress' && (
                            <Button
                              size="sm"
                              onClick={() => updateSubmissionStatus(submission.id, 'resolved')}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Resolve
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedSubmission(submission);
                              setShowResponseForm(true);
                            }}
                            className="border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white"
                          >
                            <Reply className="w-3 h-3 mr-1" />
                            Reply
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submission Details + Thread Dialog */}
      <Dialog open={showDetails} onOpenChange={(open) => { setShowDetails(open); if (!open) { setThreadMessages([]); setResponseMessage(''); } }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="h-5 w-5 text-purple-400" />
              Support Thread
            </DialogTitle>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-4">
              {/* Header info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-400">From: </span>
                  <span className="text-white">{selectedSubmission.userEmail}</span>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(selectedSubmission.status)}
                  {getStatusBadge(selectedSubmission.status)}
                </div>
                <div className="col-span-2">
                  <span className="text-gray-400">Subject: </span>
                  <span className="text-white font-medium">{selectedSubmission.subject}</span>
                </div>
              </div>

              {/* Original message */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Original message</label>
                <div className="bg-gray-800 p-3 rounded-lg border border-gray-700 mt-1">
                  <p className="text-white whitespace-pre-wrap text-sm">{selectedSubmission.message}</p>
                  <p className="text-gray-500 text-xs mt-2">
                    {selectedSubmission.createdAt?.toDate ? formatUkDateTime(selectedSubmission.createdAt) : ''}
                  </p>
                </div>
              </div>

              {/* Conversation thread */}
              {(loadingThread || threadMessages.length > 0) && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Conversation</label>
                  {loadingThread ? (
                    <div className="text-gray-400 text-sm py-2">Loading thread…</div>
                  ) : (
                    <div className="space-y-2 mt-1">
                      {threadMessages.map(msg => (
                        <div
                          key={msg.id}
                          className={`p-3 rounded-lg border text-sm ${
                            msg.fromAdmin
                              ? 'bg-blue-900/30 border-blue-700/50 ml-4'
                              : 'bg-purple-900/30 border-purple-700/50 mr-4'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className={`font-semibold text-xs ${msg.fromAdmin ? 'text-blue-300' : 'text-purple-300'}`}>
                              {msg.fromAdmin ? '🛡 Admin (YSR)' : '🎵 Artist'}
                            </span>
                            <span className="text-gray-500 text-xs">
                              {msg.createdAt?.toDate ? formatUkDateTime(msg.createdAt) : ''}
                            </span>
                          </div>
                          <p className="text-white whitespace-pre-wrap">{msg.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Inline reply form */}
              {selectedSubmission.status !== 'resolved' && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Reply to artist</label>
                  <p className="text-xs text-gray-500 mb-1 mt-0.5">
                    The artist will receive an email notification and can view your reply in their account.
                  </p>
                  <Textarea
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    placeholder="Type your reply…"
                    className="bg-gray-800 border-gray-600 text-white placeholder-gray-400 min-h-[100px] mt-1"
                  />
                  <div className="flex gap-2 mt-2">
                    <Button
                      onClick={() => submitResponse(false)}
                      disabled={isSubmittingResponse || !responseMessage.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      <Send className="w-3 h-3 mr-1" />
                      {isSubmittingResponse ? 'Sending…' : 'Send Reply'}
                    </Button>
                    <Button
                      onClick={() => submitResponse(true)}
                      disabled={isSubmittingResponse || !responseMessage.trim()}
                      className="bg-green-600 hover:bg-green-700"
                      size="sm"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Send & Mark Resolved
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t border-gray-800">
                {selectedSubmission.status === 'new' && (
                  <Button size="sm" onClick={() => { updateSubmissionStatus(selectedSubmission.id, 'in_progress'); }} className="bg-yellow-600 hover:bg-yellow-700">
                    Mark In Progress
                  </Button>
                )}
                {selectedSubmission.status === 'in_progress' && (
                  <Button size="sm" onClick={() => { updateSubmissionStatus(selectedSubmission.id, 'resolved'); }} className="bg-green-600 hover:bg-green-700">
                    Mark Resolved
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => setShowDetails(false)} className="border-gray-600 text-gray-300 hover:bg-gray-700 ml-auto">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Response Form Dialog */}
      <Dialog open={showResponseForm} onOpenChange={(open) => {
        setShowResponseForm(open);
        if (!open) {
          setResponseMessage('');
          setSelectedSubmission(null);
        }
      }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Reply className="h-5 w-5 text-blue-400" />
              Reply to Support Request
            </DialogTitle>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-4">
              <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <h4 className="font-medium text-white mb-2">Original Request:</h4>
                <p className="text-gray-300 text-sm mb-1"><strong>Subject:</strong> {selectedSubmission.subject}</p>
                <p className="text-gray-300 text-sm"><strong>From:</strong> {selectedSubmission.userEmail}</p>
                <div className="mt-3 p-3 bg-gray-700 rounded border border-gray-600">
                  <p className="text-gray-200 text-sm whitespace-pre-wrap">{selectedSubmission.message}</p>
                </div>
              </div>
              
              <div>
                <Label htmlFor="response" className="text-gray-300">Your Response</Label>
                <Textarea
                  id="response"
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder="Type your response to the artist..."
                  className="bg-gray-800 border-gray-600 text-white placeholder-gray-400 min-h-[120px] mt-2"
                  required
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={() => submitResponse(false)}
                  disabled={isSubmittingResponse || !responseMessage.trim()}
                  className="bg-blue-600 hover:bg-blue-700 flex-1"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSubmittingResponse ? 'Sending...' : 'Send Response'}
                </Button>
                <Button
                  onClick={() => submitResponse(true)}
                  disabled={isSubmittingResponse || !responseMessage.trim()}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Send & Resolve
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowResponseForm(false);
                    setResponseMessage('');
                  }}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactSubmissionsManager;
